/*
 * decaffeinate suggestions:
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import $ from 'jquery';

import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import EngComponent from 'constants/EngComponent';
import {count} from 'metrics/Metrics';
import {Response} from 'notifications';
import {getXHRHeaders, HTTPHeaders} from 'util/Net';

export const MAX_FAIL_RETRY_ATTEMPTS = 6;
const TASK_REQUEST_DONE = 25;
const MAX_TOTAL_WAIT_TIME = 676500; // 11.275 minutes

/**
 * Poll interval in milliseconds. This is a small number so that for low latency tasks,
 * we poll frequently enough that the user does not wait longer than necessary.
 */
const DEFAULT_WAIT_TIME = 500;

/**
 * Backoff for poll interval in milliseconds. If the task has long runtime (>1min),
 * polling every DEFAULT_WAIT_TIME is wasteful.
 */
const POLL_INTERVAL_LINEAR_BACKOFF = 300; // Increase waitTime by 300ms every poll.

/**
 * Maxiumum poll interval in milliseconds. To avoid linear backoff resulting in very long poll intervals,
 * esp. if such tasks report progress to the front-end, we cap the poll interval to 10s (6x per minute).
 */
export const MAX_POLL_INTERVAL = 10000;

export interface TaskMeta {
  taskURL?: string;
  initialReqCorrelationId?: string | null;
  engComponent?: EngComponent | null;
}

export const REQUEST_TOOK_TOO_LONG = 'Your request took too long to complete.';

interface PollOptions<T = unknown> {
  /** Information about the task for observability */
  taskMeta?: TaskMeta;
  /** Initial poll interval in milliseconds. waitTime increases by 300ms every poll. Default: 500ms */
  waitTime?: number;
  /** Maximum total poll time before assuming task timed out. Default: 11.275 minutes. */
  maxTotalWaitTime?: number;
  /** % completion to consider when the task has started. Default: {@link TASK_REQUEST_DONE}  */
  taskRequestDone?: number;

  /**
   * An optional callback to invoke custom behavior when the task status is `PENDING`.
   *
   * If not supplied, the default behavior is to continue polling when the task status is `PENDING`.
   *
   * @param deferred - The deferred object to resolve/reject/notify the polling operation.
   * @param retry - A function to continue polling. It consumes the number of failed retries so far.
   * @param pollStats - Information about the current polling operation.
   */
  onPendingStatus?: (
    deferred: JQueryDeferred<T>,
    retry: (nextFailRetries: number) => void,
    pollStats: Required<PollStats>
  ) => Promise<void>;
}

interface PollStats {
  totalWaitTime?: number;
  failRetries?: number;
}

function poll<T = unknown>(
  url: string,
  deferred: JQueryDeferred<T>,
  {
    taskMeta = {},
    waitTime = DEFAULT_WAIT_TIME,
    maxTotalWaitTime = MAX_TOTAL_WAIT_TIME,
    taskRequestDone = TASK_REQUEST_DONE,
    onPendingStatus,
  }: PollOptions<T>,
  {totalWaitTime = 0, failRetries = 0}: PollStats = {}
): void {
  function retry(nextFailRetries: number): void {
    // Linearly increase waitTime up to MAX_POLL_INTERVAL.
    const nextWaitTime = Math.min(MAX_POLL_INTERVAL, waitTime + POLL_INTERVAL_LINEAR_BACKOFF);
    if (totalWaitTime > maxTotalWaitTime) {
      deferred.reject(flagTaskError({userMessage: REQUEST_TOOK_TOO_LONG}));
      count('task_runner.poll_timeout', 1, {task_url: taskMeta.taskURL, component: taskMeta.engComponent});
      return;
    }
    setTimeout(() => {
      poll(
        url,
        deferred,
        {
          taskMeta,
          waitTime: nextWaitTime,
          maxTotalWaitTime,
          taskRequestDone,
          onPendingStatus,
        },
        {
          totalWaitTime: nextWaitTime + totalWaitTime,
          failRetries: nextFailRetries,
        }
      );
    }, waitTime);
  }

  function retryOrReject<T>(e: T): void {
    if (failRetries >= MAX_FAIL_RETRY_ATTEMPTS) {
      deferred.reject(e);
      return;
    }
    retry(failRetries + 1);
  }

  $.ajax({
    url,
    type: 'GET',
    // Pass in taskMeta to include in Sentry context for debugging.
    data: taskMeta,
  })
    .done((res: TaskStatusResponse<T>) => {
      if (res.status === 'SUCCESS') {
        deferred.resolve(res.result);
      } else if (res.status === 'FAILURE') {
        deferred.reject(flagTaskError(getFailedTaskErrorResponse(res), taskMeta));
      } else if (onPendingStatus && res.status === 'PENDING') {
        onPendingStatus(deferred, retry, {totalWaitTime, failRetries}).catch((e) => {
          retryOrReject(e);
        });
      } else {
        if (res.progress != null) {
          const progressPercent = taskRequestDone + ((100 - taskRequestDone) * res.progress) / 100;
          deferred.notify(progressPercent);
        }
        retry(failRetries);
      }
    })
    .fail((res) => {
      retryOrReject(flagTaskError(res, taskMeta));
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ongoingTasks: {[key: string]: JQueryPromise<any>} = {};

export interface OngoingTaskResponse {
  status_url: string;
}

export interface FailedTaskResponse {
  status: 'FAILURE';
  error: FailedTaskError;
}

export type TaskStatusResponse<T> =
  | {status: 'STARTED'; progress?: number}
  | {status: 'PENDING' | 'QUEUED'; progress?: number}
  | {status: 'SUCCESS'; result: T}
  | FailedTaskResponse;

export function getTaskMeta(xhr: JQueryXHR, taskURL: string | undefined): TaskMeta {
  return {
    taskURL,
    initialReqCorrelationId: xhr.getResponseHeader('X-Debug-Req-Correlation-ID'),
    engComponent: xhr.getResponseHeader('X-Eng-Component') as EngComponent,
  };
}

function runTask(
  args: JQueryAjaxSettings,
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME,
  analyticsMetadata?: AnalyticsEventMetadata,
  extraHeaders?: HTTPHeaders
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deferred = $.Deferred<any>();
  const promise = deferred.promise();

  function requestProgress(e: ProgressEvent): void {
    if (e.lengthComputable) {
      deferred.notify((TASK_REQUEST_DONE * e.loaded) / e.total);
    }
  }

  // Update this if getPOSTHeaders, etc. in Net.ts diverge
  args.headers = {...args.headers, ...getXHRHeaders(analyticsMetadata), ...extraHeaders};

  let nativeXHR: XMLHttpRequest | null = null;
  $.ajax({
    dataType: 'json',
    xhr(): XMLHttpRequest {
      // The only way to access the final redirect URL is to override the default xhr() implementation.
      // See https://github.com/jquery/jquery/issues/4339
      nativeXHR = new window.XMLHttpRequest();
      // Register progress callback on new XHR the same way it is done in jquery.ajax-progress.js
      nativeXHR.addEventListener('progress', requestProgress, false);
      return nativeXHR;
    },
    ...args,
  })
    .done((res: OngoingTaskResponse, statusText: string, xhr: JQueryXHR) => {
      assert(res.status_url != null, 'TaskRunner expected response to contain a status_url', {
        requestURL: args.url,
        responseURL: nativeXHR?.responseURL,
        status: xhr.status,
      });
      deferred.notify(TASK_REQUEST_DONE);
      ongoingTasks[res.status_url] = promise;
      promise.always(() => delete ongoingTasks[res.status_url]);
      poll(res.status_url, deferred, {
        taskMeta: getTaskMeta(xhr, args.url),
        maxTotalWaitTime,
      });
    })
    .fail((res: Response) => {
      deferred.reject(flagTaskError(res));
    });
  return promise;
}

function postJSON(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return runTask(
    {
      contentType: 'application/json',
      data: JSON.stringify(data),
      dataType: 'json',
      type: 'POST',
      url,
    },
    maxTotalWaitTime,
    analyticsMetadata,
    extraHeaders
  );
}

async function postJSONPromise(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  return await Promise.resolve(postJSON({url, data, analyticsMetadata, extraHeaders}, maxTotalWaitTime));
}

function patchJSON(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return runTask(
    {
      contentType: 'application/json',
      data: JSON.stringify(data),
      dataType: 'json',
      type: 'PATCH',
      url,
    },
    maxTotalWaitTime,
    analyticsMetadata,
    extraHeaders
  );
}

function patchJSONPromise(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  return Promise.resolve(patchJSON({url, data, analyticsMetadata, extraHeaders}, maxTotalWaitTime));
}

function getJSON(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return runTask(
    {
      contentType: 'application/json',
      data: JSON.stringify(data),
      dataType: 'json',
      type: 'GET',
      url,
    },
    maxTotalWaitTime,
    analyticsMetadata,
    extraHeaders
  );
}

function getJSONPromise(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  return Promise.resolve(getJSON({url, data, analyticsMetadata, extraHeaders}, maxTotalWaitTime));
}

function deleteJSON(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return runTask(
    {
      contentType: 'application/json',
      data: JSON.stringify(data),
      dataType: 'json',
      type: 'DELETE',
      url,
    },
    maxTotalWaitTime,
    analyticsMetadata,
    extraHeaders
  );
}

function deleteJSONPromise(
  {
    url,
    data,
    analyticsMetadata,
    extraHeaders,
  }: {url: string; data?: Object; analyticsMetadata?: AnalyticsEventMetadata; extraHeaders?: HTTPHeaders},
  maxTotalWaitTime: number = MAX_TOTAL_WAIT_TIME
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  return Promise.resolve(deleteJSON({url, data, analyticsMetadata, extraHeaders}, maxTotalWaitTime));
}

function resumeTask<R = unknown>(statusURL: string, pollOptions: PollOptions = {}): JQueryPromise<R> {
  const deferred = $.Deferred<R>();
  if (ongoingTasks[statusURL] != null) {
    return ongoingTasks[statusURL];
  }
  poll(statusURL, deferred, pollOptions);
  return deferred.promise();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resumeTaskPromise(statusURL: string, pollOptions: PollOptions = {}): Promise<any> {
  return Promise.resolve(resumeTask(statusURL, pollOptions));
}

export interface FailedTaskMetadata {
  isBenchlingTaskError: true;
  taskURL?: string;
  initialReqCorrelationId?: string | null;
  engComponent?: EngComponent | null;
}

export interface FailedTaskError {
  status: number;
  responseText: string;
  userMessage?: string;
}
export interface FailedTaskErrorResponse extends FailedTaskError {
  /** The status of the failed celery task */
  taskStatus: 'FAILURE';
}

export function getFailedTaskErrorResponse(res: FailedTaskResponse): FailedTaskErrorResponse {
  return {...res.error, taskStatus: res.status};
}

interface TimeoutPseudoResponse {
  userMessage: string;
}

interface InvalidTaskPseudoResponse {
  /** The task is no longer valid */
  invalidTask: true;
}

/**
 * Flag an error response as a Benchling task error.
 *
 * When flagging an error from a {@link FailedTaskResponse} use {@link getFailedTaskErrorResponse}.
 */
function flagTaskError<
  T extends Response | FailedTaskErrorResponse | TimeoutPseudoResponse | InvalidTaskPseudoResponse,
>(res: T, taskMeta: TaskMeta = {}): T & FailedTaskMetadata {
  return {
    ...res,
    isBenchlingTaskError: true,
    taskURL: taskMeta.taskURL,
    initialReqCorrelationId: taskMeta.initialReqCorrelationId,
    engComponent: taskMeta.engComponent,
  };
}

export {
  flagTaskError,
  getJSON,
  getJSONPromise,
  patchJSON,
  patchJSONPromise,
  postJSON,
  postJSONPromise,
  deleteJSON,
  deleteJSONPromise,
  runTask,
  resumeTask,
  resumeTaskPromise,
};
